import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ArrowUp } from "lucide-react";

const SCROLL_THRESHOLD = 450;

export default function ScrollToTopButton() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  // Reset visibility when route changes
  useEffect(() => {
    setVisible(false);
  }, [location.pathname]);

  // Track scroll position with optimized throttling
  useEffect(() => {
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const shouldShow = window.scrollY > SCROLL_THRESHOLD;
          setVisible((prev) => (prev !== shouldShow ? shouldShow : prev));
          ticking = false;
        });
        ticking = true;
      }
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = useCallback(() => {
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  }, []);

  return (
    <button
      type="button"
      onClick={scrollToTop}
      data-testid="scroll-to-top"
      aria-label="Voltar ao topo"
      title="Voltar ao topo"
      className={`fixed z-30 inline-flex items-center justify-center gap-2 rounded-full border border-border/80 bg-card/90 backdrop-blur-md shadow-md transition-all duration-300 ease-out hover:border-gold/60 hover:bg-secondary/80 hover:shadow-lg active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 bottom-[calc(4.75rem+env(safe-area-inset-bottom,0px))] right-4 p-2.5 sm:px-3.5 sm:py-2 xl:bottom-8 xl:right-8 ${
        visible
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-4 pointer-events-none"
      }`}
    >
      <ArrowUp className="h-4 w-4 shrink-0 text-gold" strokeWidth={2.25} />
      <span className="hidden sm:inline text-xs font-medium text-foreground tracking-wide">
        Voltar ao topo
      </span>
    </button>
  );
}
